/**
 * Compositor de la carta de respuesta del Ratón Pérez.
 *
 * La guía tenía una plantilla estática con huecos `____`: para usarla había que
 * imprimirla y escribirla a mano, o reteclearla entera. Esto la convierte en
 * algo que se rellena una vez y sale terminado, que es lo que el padre necesita
 * la noche del diente.
 *
 * Todo son funciones puras sobre strings: el render (canvas) y el formulario
 * viven aparte. El nombre del niño NUNCA sale del dispositivo — no hay llamada
 * de red en ningún punto de este flujo, a propósito, porque son datos de un
 * menor y no los necesitamos para nada.
 *
 * Hoy solo existe la variante es/raton-perez, que es la guía que la usa.
 * Ampliar a hada/pt/en es añadir entradas a `LETTER_COPY`, no tocar la lógica.
 */

export type ToothKind = 'first' | 'next' | 'molar'

export type ToothLetterInput = {
  childName: string
  /** Años del niño. Se teje en la línea del primer diente si viene. */
  age?: number
  tooth: ToothKind
  /** Cómo se cayó, en palabras del padre: "comiendo una manzana". */
  howItFell?: string
  /** Un logro reciente: "ha aprendido a nadar". */
  achievement?: string
  /** Lo que deja el ratón, ya formateado: "2 €". */
  amount?: string
}

export type ComposedLetter = {
  greeting: string
  paragraphs: string[]
  signoff: string
  signature: string
}

type LetterCopy = {
  greeting: string
  firstTooth: string
  firstToothWithAge: string
  nextTooth: string
  molar: string
  howItFell: string
  brushing: string
  achievement: string
  amount: string
  closing: string
  signoff: string
  signature: string
  yearOne: string
  yearMany: string
}

const LETTER_COPY: LetterCopy = {
  greeting: '¡Hola, {name}!',
  firstTooth: '¡Tu primer diente! Llevaba días esperando esta noche.',
  firstToothWithAge:
    '¡Tu primer diente! Llevaba días esperando esta noche: {years} y ya estrenas el club de los dientes caídos.',
  nextTooth: '¡Otro diente para mi colección! Este ya lo estaba esperando.',
  molar: '¡Una muela! Son las más difíciles de todas, y las que más pesan en mi saco.',
  howItFell: 'Me han contado que se te cayó {howItFell}. Menuda historia para recordar.',
  brushing: 'Se nota que lo has cepillado bien: brillaba tanto que lo encontré a la primera.',
  achievement: 'Y los otros ratones me han dicho que {achievement}. ¡Enhorabuena de mi parte!',
  amount: 'Debajo de la almohada te dejo {amount}. Es mi manera de darte las gracias.',
  closing:
    'Tengo que salir volando: esta noche me quedan {remaining} dientes por recoger. Sigue cepillándote cada día, que los próximos también los quiero relucientes.',
  signoff: 'Con cariño (y un poquito de queso),',
  signature: 'Ratón Pérez',
  yearOne: '{age} año',
  yearMany: '{age} años',
}

/** Sustituye `{clave}` por su valor. Las claves ausentes se dejan intactas. */
export function interpolate(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (match, key: string) => values[key] ?? match)
}

/**
 * Cuántos dientes le quedan al ratón esa noche. Derivado del nombre en vez de
 * aleatorio: da variedad entre niños pero es estable para el mismo niño (si el
 * padre regenera la carta, el ratón no se contradice) y hace la función
 * testeable sin controlar el reloj ni el generador aleatorio.
 */
export function remainingTeethFor(childName: string): number {
  const seed = [...childName.trim().toLowerCase()].reduce(
    (total, character) => total + character.charCodeAt(0),
    0
  )
  return 180 + (seed % 90)
}

function openingFor(input: ToothLetterInput): string {
  if (input.tooth === 'molar') return LETTER_COPY.molar
  if (input.tooth === 'next') return LETTER_COPY.nextTooth
  if (input.age === undefined) return LETTER_COPY.firstTooth

  const yearsTemplate = input.age === 1 ? LETTER_COPY.yearOne : LETTER_COPY.yearMany
  const years = interpolate(yearsTemplate, { age: String(input.age) })
  return interpolate(LETTER_COPY.firstToothWithAge, { years })
}

/** Texto opcional del padre: se ignora si viene vacío o solo con espacios. */
function filled(value: string | undefined): string | null {
  const trimmed = value?.trim()
  return trimmed ? trimmed : null
}

export function composeToothLetter(input: ToothLetterInput): ComposedLetter {
  const name = input.childName.trim()
  const paragraphs = [openingFor(input)]

  const howItFell = filled(input.howItFell)
  if (howItFell) paragraphs.push(interpolate(LETTER_COPY.howItFell, { howItFell }))

  paragraphs.push(LETTER_COPY.brushing)

  const achievement = filled(input.achievement)
  if (achievement) paragraphs.push(interpolate(LETTER_COPY.achievement, { achievement }))

  const amount = filled(input.amount)
  if (amount) paragraphs.push(interpolate(LETTER_COPY.amount, { amount }))

  paragraphs.push(
    interpolate(LETTER_COPY.closing, { remaining: String(remainingTeethFor(name)) })
  )

  return {
    greeting: interpolate(LETTER_COPY.greeting, { name }),
    paragraphs,
    signoff: LETTER_COPY.signoff,
    signature: LETTER_COPY.signature,
  }
}

/** Nombre de fichero de la descarga. Sin el nombre del niño: acaba en su carrete. */
export function letterFilename(): string {
  return 'carta-del-raton-perez.png'
}
